<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';
import { TaskStatus, type ContractGenerationDraftSummary } from '@/api';
import { IconMore } from '@/components/design/icons';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaVariantChip from '@/components/design/domain/templating/GaiaVariantChip.vue';
import { templateValidationChipMap } from '@/components/design/statusMaps';
import {
  gaiaContentWidth,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
} from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { useRowDelete } from '@/composables/useRowDelete';
import { DRAFT_PAGE_SIZES, DraftsService } from '@/services/contractGeneration/draftsService';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

/**
 * The Drafts page — Word's "Open" screen for contract templating.
 *
 * One list of the documents being drafted from templates, which open back
 * into the wizard exactly where they were left. A draft is a vault file, so
 * it is also in the vault, and once saved it is a document there and leaves
 * this list. "New document" is the only way into the wizard from here;
 * everything else on the page is about finding a draft again.
 *
 * Search, sort and paging are server-side and URL-synced.
 */

const router = useRouter();
const message = useGaiaToast();

const { currentPage, pageSize, searchQuery, sortKey, sortDirection, sortActivated } =
  useListQuerySync({
    defaultPageSize: 25,
    allowedPageSizes: DRAFT_PAGE_SIZES,
    defaultSortKey: 'created_at',
    defaultSortDirection: 'desc',
  });

const drafts = ref<ContractGenerationDraftSummary[]>([]);
const totalCount = ref<number>(0);
const loading = ref<boolean>(false);
const loadError = ref<string | null>(null);
/** Set when a refresh fails with rows still on screen — see `fetchDrafts`. */
const staleSince = ref<string | null>(null);

const rowDelete = useRowDelete<ContractGenerationDraftSummary>();

// --- derived -----------------------------------------------------------------
const hasQuery = computed(() => searchQuery.value.trim().length > 0);
const narrowed = computed(() => hasQuery.value);

const countLabel = computed(() => {
  if (loading.value) return 'Loading';
  return `${totalCount.value} ${totalCount.value === 1 ? 'draft' : 'drafts'}`;
});

const showEmpty = computed(
  () => !loading.value && !loadError.value && drafts.value.length === 0 && !narrowed.value
);
const showNoMatch = computed(
  () => !loading.value && !loadError.value && drafts.value.length === 0 && narrowed.value
);
const showRows = computed(() => !loading.value && drafts.value.length > 0);

const tableColumns: GaiaDataColumn[] = [
  { key: 'file_name', label: 'Document', width: 'minmax(280px, 2fr)', sortable: true },
  { key: 'created_at', label: 'Last activity', width: '190px', sortable: true },
];

const skeletonCells = ['62%', '80%'];

// --- data --------------------------------------------------------------------
/**
 * Which fetch is the current one. A superseded response must not land: it
 * would paint rows for a filter nobody is looking at any more, or clear the
 * spinner while the request the user is waiting on is still in flight.
 */
let requestSeq = 0;

async function fetchDrafts(): Promise<void> {
  const seq = ++requestSeq;
  loading.value = true;
  loadError.value = null;
  try {
    const page = await DraftsService.search({
      text: searchQuery.value,
      page: currentPage.value,
      pageSize: pageSize.value,
      sortKey: sortKey.value,
      sortDirection: sortDirection.value,
    });
    if (seq !== requestSeq) return;
    drafts.value = page.results;
    totalCount.value = page.result_count;
    staleSince.value = null;
  } catch (error) {
    logger.error('Error fetching drafts:', error);
    if (seq !== requestSeq) return;
    // Rows already on screen survive the failure behind a stale banner; only a
    // cold failure takes over the card.
    if (drafts.value.length > 0) {
      staleSince.value = new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else {
      loadError.value = extractErrorDetail(error, 'Couldn’t load drafts');
    }
  } finally {
    if (seq === requestSeq) loading.value = false;
  }
}

/**
 * One fetch per turn of the loop. A sort or search resets the page and the
 * filter together, and each has a watcher; coalescing to the next tick is
 * what keeps that one request instead of two.
 */
let fetchScheduled = false;
function requestFetch(): void {
  if (fetchScheduled) return;
  fetchScheduled = true;
  void nextTick(() => {
    fetchScheduled = false;
    void fetchDrafts();
  });
}

/** Any change that changes *which* drafts match starts again from page 1. */
function reloadFromFirstPage(): void {
  currentPage.value = 1;
  requestFetch();
}

function applySearch(): void {
  reloadFromFirstPage();
}

function clearNarrowing(): void {
  searchQuery.value = '';
  reloadFromFirstPage();
}

/** A new page size re-slices the result set, so the list goes back to page 1. */
function setPageSize(size: number): void {
  pageSize.value = size;
  currentPage.value = 1;
}

function onSort(key: string): void {
  if (sortActivated.value && sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortActivated.value = true;
    sortKey.value = key;
    // A date opens newest-first; the name opens A–Z.
    sortDirection.value = key === 'file_name' ? 'asc' : 'desc';
  }
  reloadFromFirstPage();
}

watch([currentPage, pageSize], () => {
  if (currentPage.value > 0 && pageSize.value > 0) requestFetch();
});

onMounted(() => {
  void fetchDrafts();
});

// --- formatting --------------------------------------------------------------
/** "Sep 7, 02:32 PM" — the time matters for work in progress, the year rarely does. */
function formatActivityTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** When a draft was last worked on. */
function activityLabel(draft: ContractGenerationDraftSummary): string {
  return draft.updated_at
    ? `Edited ${formatActivityTime(draft.updated_at)}`
    : `Started ${formatActivityTime(draft.created_at)}`;
}

/**
 * A draft whose template broke or went back into validation can be opened
 * but not generated — the row says so before the user clicks through.
 */
function templateWarning(
  draft: ContractGenerationDraftSummary
): { tone: 'warning' | 'alert'; label: string } | null {
  const status = draft.template_status;
  if (!status || status === TaskStatus.DONE) return null;
  const chip = templateValidationChipMap[status];
  return {
    tone: chip.tone === 'alert' ? 'alert' : 'warning',
    label: chip.tone === 'alert' ? 'Template broken' : 'Template validating',
  };
}

// --- row actions -------------------------------------------------------------
function startNewDocument(): void {
  void router.push({ name: 'contract-generation-wizard-new' });
}

function draftRoute(draft: ContractGenerationDraftSummary): RouteLocationRaw {
  return { name: 'contract-generation-draft', params: { draftUid: draft.uid } };
}

function openDraft(draft: ContractGenerationDraftSummary): void {
  void router.push(draftRoute(draft));
}

async function duplicateDraft(draft: ContractGenerationDraftSummary): Promise<void> {
  try {
    const copy = await DraftsService.duplicate(draft.uid);
    message.success(`Copy created — you're now editing “${copy.file_name}”.`);
    void router.push({ name: 'contract-generation-draft', params: { draftUid: copy.uid } });
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to duplicate the draft.'));
  }
}

/** The row's ⋯ menu. */
function rowMenuOptions(): Record<string, unknown>[] {
  return [
    { key: 'open', label: 'Continue editing' },
    { key: 'duplicate', label: 'Duplicate' },
    { key: 'divider', type: 'divider' },
    { key: 'delete', label: 'Delete draft', props: gaiaDangerOption },
  ];
}

function onRowMenu(key: string, draft: ContractGenerationDraftSummary): void {
  if (key === 'open') {
    openDraft(draft);
    return;
  }
  if (key === 'duplicate') {
    void duplicateDraft(draft);
    return;
  }
  rowDelete.arm(draft);
}

async function deleteDraftRow(draft: ContractGenerationDraftSummary): Promise<void> {
  await rowDelete.run(draft, {
    work: async () => {
      await DraftsService.delete(draft.uid);
      message.success(`Draft “${draft.file_name}” deleted.`);
      await fetchDrafts();
    },
    errorText: () => 'Couldn’t delete this draft. Nothing was changed.',
  });
}

// --- chrome ------------------------------------------------------------------
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const tileSurface = gaiaNeutral.surfaceTint;
const tileBorder = gaiaNeutral.borderMuted;
const tileInk = gaiaNeutral.textTertiary;
const nameSize = gaiaFontSize.row;
const nameWeight = String(gaiaFontWeight.semibold);
const inkColor = gaiaNeutral.ink;
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.draft }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="18">
      <!-- Title + what the page is, the primary action pushed right. -->
      <n-flex align="flex-start" :size="[24, 12]">
        <n-flex vertical :size="6" :style="{ flex: '1 1 320px', minWidth: 0 }">
          <n-flex align="baseline" :size="10" :style="{ minWidth: 0 }">
            <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
              {{ navLabels.draft }}
            </gaia-header>
            <gaia-count-label v-if="!loading">{{ countLabel }}</gaia-count-label>
          </n-flex>
          <gaia-text size="body" tone="tertiary" :style="{ maxWidth: '64ch' }">
            Documents you are drafting from templates. Everything you enter is saved as you go, so
            you can leave a draft and pick it up later. Saved documents are in your vault.
          </gaia-text>
        </n-flex>

        <n-flex align="center" justify="end" :size="10" :style="{ marginLeft: 'auto' }">
          <gaia-search-input
            v-model:value="searchQuery"
            variant="toolbar"
            placeholder="Search drafts by name"
            :style="{ width: '288px' }"
            @search="applySearch"
          />
          <gaia-button
            type="primary"
            size="medium"
            data-tour="drafts.new-button"
            @click="startNewDocument"
          >
            <template #icon>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
            </template>
            New document
          </gaia-button>
        </n-flex>
      </n-flex>

      <gaia-data-table
        data-tour="drafts.list"
        :columns="tableColumns"
        :sort-key="sortKey"
        :sort-direction="sortDirection === 'asc' ? 'asc' : 'desc'"
        :sort-active="sortActivated"
        :action-width="152"
        @sort="onSort"
      >
        <template #banner>
          <gaia-banner v-if="staleSince" plain type="error">
            Couldn’t refresh — showing drafts as of {{ staleSince }}.
            <template #action>
              <gaia-button text size="tiny" @click="fetchDrafts">Retry</gaia-button>
            </template>
          </gaia-banner>
        </template>

        <template #default="{ rowStyle }">
          <gaia-table-skeleton
            v-if="loading"
            :row-style="rowStyle"
            :cells="skeletonCells"
            :rows="6"
            :row-height="64"
          />

          <gaia-empty-state v-else-if="loadError" status="alert" title="Couldn’t load drafts">
            The server didn’t respond in time. Nothing was changed — retrying is safe.
            <template #meta>{{ loadError }}</template>
            <template #actions>
              <gaia-button type="primary" size="small" @click="fetchDrafts">Retry</gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="showEmpty" title="No drafts yet">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M4 2.5h6l4 4v9H4z" />
                <path d="M10 2.5v4h4M7 10h4M7 12.5h3" />
              </svg>
            </template>
            Start a document from one of your templates. Your progress is saved automatically, so
            you can stop at any point and come back to it here.
            <template #actions>
              <gaia-button type="primary" size="medium" @click="startNewDocument">
                New document
              </gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="showNoMatch" title="No drafts match">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              >
                <circle cx="8" cy="8" r="5" />
                <path d="M11.8 11.8 15.5 15.5" />
              </svg>
            </template>
            Search covers document names. Try a shorter term, or show all drafts again.
            <template #actions>
              <gaia-button size="small" @click="clearNarrowing">Show all drafts</gaia-button>
            </template>
          </gaia-empty-state>

          <template v-else-if="showRows">
            <template v-for="draft in drafts" :key="draft.uid">
              <!-- A row in trouble is replaced by its strip, never stacked with it. -->
              <gaia-table-row-state
                v-if="rowDelete.stateOf(draft.uid) === 'confirm'"
                variant="confirm"
                :title="`Delete “${draft.file_name}”?`"
                confirm-label="Delete draft"
                @cancel="rowDelete.cancel()"
                @confirm="deleteDraftRow(draft)"
              >
                The draft and everything you entered in it are removed. This can’t be undone.
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(draft.uid) === 'busy'"
                variant="busy"
                note="Undo not available"
              >
                Deleting “{{ draft.file_name }}”…
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(draft.uid) === 'error'"
                variant="error"
                @retry="deleteDraftRow(draft)"
                @dismiss="rowDelete.dismissError()"
              >
                {{ rowDelete.errorTextOf(draft.uid) }}
              </gaia-table-row-state>

              <div v-else class="row" :style="rowStyle" @click="openDraft(draft)">
                <div class="name-cell">
                  <div class="tile">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M4 2.5h6.5L13 5v8.5H4z" />
                      <path d="M6 6.5h4M6 9h2.5" />
                    </svg>
                  </div>
                  <div class="name-stack">
                    <!-- A real link so the tour can click it (`.row .name-link`). -->
                    <router-link :to="draftRoute(draft)" class="name-link" @click.stop>
                      {{ draft.file_name }}
                    </router-link>
                    <n-flex align="center" :size="6" :wrap="false" :style="{ minWidth: 0 }">
                      <gaia-text truncate size="micro" tone="subtle">
                        From {{ draft.template_name }}
                      </gaia-text>
                      <gaia-variant-chip :name="draft.variant_name" />
                      <gaia-status-chip
                        v-if="templateWarning(draft)"
                        :tone="templateWarning(draft)!.tone"
                      >
                        {{ templateWarning(draft)!.label }}
                      </gaia-status-chip>
                    </n-flex>
                  </div>
                </div>

                <div class="cell">
                  <gaia-text
                    truncate
                    size="dense"
                    tone="tertiary"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    {{ activityLabel(draft) }}
                  </gaia-text>
                </div>

                <n-flex justify="end" align="center" :size="4" :wrap="false" @click.stop>
                  <gaia-button size="small" @click="openDraft(draft)"> Continue </gaia-button>
                  <gaia-dropdown
                    :options="rowMenuOptions()"
                    placement="bottom-end"
                    @select="(key: string) => onRowMenu(key, draft)"
                  >
                    <gaia-icon-button :dimension="28" quaternary title="More">
                      <n-icon :component="IconMore" :size="16" />
                    </gaia-icon-button>
                  </gaia-dropdown>
                </n-flex>
              </div>
            </template>
          </template>
        </template>

        <template v-if="showRows" #footer>
          <gaia-pagination
            :page="currentPage"
            :page-size="pageSize"
            :total="totalCount"
            :page-sizes="DRAFT_PAGE_SIZES"
            @update:page="(page: number) => (currentPage = page)"
            @update:page-size="setPageSize"
          />
        </template>
      </gaia-data-table>
    </n-flex>
  </Layout>
</template>

<style scoped>
/* A body row rides the grid template GaiaDataTable hands out through the slot,
   so only the row's own chrome lives here. Taller than the standard 56px row:
   the name cell carries the template line under the document name. */
.row {
  display: grid;
  align-items: center;
  gap: 16px;
  min-height: 64px;
  padding: 10px 18px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.name-stack {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

/* The glyph tile marks the row as a document at a glance, next to the name
   rather than as another column. */
.tile {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid v-bind(tileBorder);
  border-radius: 8px;
  background: v-bind(tileSurface);
  color: v-bind(tileInk);
}

/* The name is the record link — bold ink, not link-blue, as on the other lists. */
.name-link {
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: v-bind(nameSize);
  font-weight: v-bind(nameWeight);
  color: v-bind(inkColor);
  text-decoration: none;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}
</style>
